import React, { useMemo, useState, useEffect } from 'react';
import {
  FileText,
  Eye,
  Edit,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  Save,
  X,
} from 'lucide-react';

import pdf from '../../../assets/images/icons/pdf.png';
import word from '../../../assets/images/icons/word.png';
import pptx from '../../../assets/images/icons/pptx.png';
import xlsx from '../../../assets/images/icons/xlsx.png';

export default function ReviewStep({
  uploadedFiles,
  extractedContents,
  selectedFile,
  onFileSelect,
  onContentUpdate,
  onBack,
  onNext,
}) {
  const [mode, setMode] = useState('preview'); // 'preview' | 'edit'
  const [editedContent, setEditedContent] = useState('');

  const selectedContent = useMemo(() => {
    if (uploadedFiles.length === 0) return null;
    const file = uploadedFiles[selectedFile];
    if (!file) return null;
    return extractedContents.find((ec) => ec.fileId === file.id);
  }, [uploadedFiles, extractedContents, selectedFile]);

  // Reset to preview mode when switching files
  useEffect(() => {
    setMode('preview');
  }, [selectedFile]);

  const handleEditClick = () => {
    setEditedContent(selectedContent?.content || '');
    setMode('edit');
  };

  const handleSave = () => {
    if (onContentUpdate && selectedContent) {
      onContentUpdate(selectedContent.fileId, editedContent);
    }
    setMode('preview');
  };

  const handleCancelEdit = () => {
    setMode('preview');
  };

  const getFileIcon = (fileName) => {
    const ext = fileName.split('.').pop()?.toLowerCase();
    if (ext === 'pdf') return pdf;
    if (ext === 'doc' || ext === 'docx') return word;
    if (ext === 'ppt' || ext === 'pptx') return pptx;
    if (ext === 'xlsx') return xlsx;
  };
  return (
    <div className="max-h-[calc(100vh-130px)] custom-scrollbar overflow-y-auto bg-zinc-950 text-white p-8">
      <div className=" mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center">
            <FileText className="w-5 h-5 mr-2" />
            <h1 className="text-lg font-medium">Document Review & Editing</h1>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setMode('preview')}
              className={`flex items-center text-sm px-3 py-1.5 rounded border transition-colors ${
                mode === 'preview'
                  ? 'bg-zinc-700 text-white border-zinc-500'
                  : 'text-gray-400 hover:text-white border-zinc-700'
              }`}
            >
              <Eye className="w-4 h-4 mr-1.5" />
              Preview
            </button>
            <button
              onClick={handleEditClick}
              disabled={!selectedContent}
              className={`flex items-center text-sm px-3 py-1.5 rounded border transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                mode === 'edit'
                  ? 'bg-zinc-700 text-white border-zinc-500'
                  : 'text-gray-400 hover:text-white border-zinc-700'
              }`}
            >
              <Edit className="w-4 h-4 mr-1.5" />
              Edit
            </button>
          </div>
        </div>

        <p className="text-gray-500 text-sm mb-6">
          Review and refine your extracted content
        </p>

        {/* Status Badge */}
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-red-950/50 border border-red-900/50 rounded px-3 py-1.5">
            <span className="text-red-400 text-xs font-medium">
              {extractedContents.filter((ec) => ec.success).length} Files Parsed
            </span>
          </div>
          {extractedContents.some((ec) => !ec.success) && (
            <div className="flex items-center text-yellow-400 text-xs">
              <AlertCircle className="w-4 h-4 mr-1" />
              Some files failed to extract
            </div>
          )}
        </div>

        {/* File Tabs */}
        

        {/* Document Content */}
        <div className="bg-[#292A2E] rounded-lg border border-zinc-800 p-8">
          {/* File tabs row — Save/Cancel live here in edit mode */}
          <div className="flex items-center justify-between gap-2 mb-6">
            <div className="flex gap-2 overflow-x-auto pb-1 flex-1 min-w-0">
              {uploadedFiles.length > 0 ? uploadedFiles.map((file, index) => (
                <button
                  key={file.id}
                  onClick={() => onFileSelect(index)}
                  className={`flex items-center gap-2 px-3 py-2 rounded text-sm whitespace-nowrap transition-colors border flex-shrink-0 ${
                    selectedFile === index
                      ? 'text-white border-red-600'
                      : 'bg-zinc-900 text-gray-400 hover:bg-zinc-800 border-zinc-700'
                  }`}
                >
                  <img src={getFileIcon(file.name)} alt={file.name} className="w-4 h-4 flex-shrink-0" />
                  <span className="truncate max-w-[200px]">{file.name}</span>
                </button>
              )) : (
                <span className="text-gray-500 text-sm">No files uploaded. Please go back to Step 1 to upload files.</span>
              )}
            </div>
            {mode === 'edit' && (
              <div className="flex gap-2 flex-shrink-0">
                <button
                  onClick={handleCancelEdit}
                  className="flex items-center gap-1.5 text-sm text-gray-400 hover:text-white border border-zinc-700 px-4 py-1.5 rounded transition-colors"
                >
                  <X className="w-4 h-4" />
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  className="flex items-center gap-1.5 text-sm bg-red-600 hover:bg-red-700 text-white px-4 py-1.5 rounded transition-colors"
                >
                  <Save className="w-4 h-4" />
                  Save
                </button>
              </div>
            )}
          </div>

          {selectedContent ? (
            <>
              <div className="mb-4">
                {selectedContent.metadata && (
                  <div className="text-sm text-gray-400 space-y-1">
                    {selectedContent.metadata.title && (
                      <div>
                        <span className="text-gray-500">Title:</span>{' '}
                        <span className="text-gray-300">{selectedContent.metadata.title}</span>
                      </div>
                    )}
                    {selectedContent.metadata.department && (
                      <div>
                        <span className="text-gray-500">Department:</span>{' '}
                        <span className="text-gray-300">{selectedContent.metadata.department}</span>
                      </div>
                    )}
                    {selectedContent.metadata.effectiveDate && (
                      <div>
                        <span className="text-gray-500">Effective Date:</span>{' '}
                        <span className="text-gray-300">{selectedContent.metadata.effectiveDate}</span>
                      </div>
                    )}
                    {selectedContent.metadata.approvedBy && (
                      <div>
                        <span className="text-gray-500">Approved By:</span>{' '}
                        <span className="text-gray-300">{selectedContent.metadata.approvedBy}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {mode === 'edit' ? (
                <div>
                  <textarea
                    value={editedContent}
                    onChange={(e) => setEditedContent(e.target.value)}
                    className="w-full bg-[#404040] text-sm text-gray-200 rounded-lg p-4 focus:outline-none focus:ring-2 focus:ring-red-500 resize-none custom-scrollbar"
                    style={{ minHeight: 'calc(100vh - 380px)' }}
                    spellCheck={false}
                  />
                </div>
              ) : (
                <div className="space-y-4 bg-[#404040] max-h-[calc(100vh-300px)] custom-scrollbar overflow-y-auto rounded-lg p-4">
                  {selectedContent.content ? (
                    <div className="text-sm leading-relaxed text-gray-300 space-y-4">
                      {selectedContent.content.split('\n\n').map((paragraph, idx) => (
                        <div key={idx}>
                          {paragraph.startsWith('•') || paragraph.match(/^\d+\./) ? (
                            <div className="pl-4">{paragraph}</div>
                          ) : (
                            <p>{paragraph}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <AlertCircle className="w-12 h-12 mx-auto mb-2 opacity-50" />
                      <p>No content extracted from this file</p>
                    </div>
                  )}
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16">
              <FileText className="w-16 h-16 mx-auto mb-4 text-gray-600 opacity-50" />
              <p className="text-gray-500">
                No file selected or content available
              </p>
              <p className="text-gray-600 text-sm mt-2">
                Please upload and process files in Step 1
              </p>
            </div>
          )}
        </div>

        {/* Navigation Buttons */}
        <div className="flex justify-between mt-6">
          <button
            onClick={onBack}
            className="flex items-center text-sm text-gray-400 hover:text-white transition-colors"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </button>
          <button
            onClick={onNext}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-2 rounded text-sm font-medium flex items-center transition-colors"
          >
            Continue
            <ChevronRight className="w-4 h-4 ml-1" />
          </button>
        </div>
      </div>
    </div>
  );
}